/**
 * 簡易予測 WMA 設定ダイアログ (§7, simple-methods AC-005a/006a/006c/016/017)。
 *
 * 加重移動平均(短期) / 同区分加重平均の Entry 行 (取込元日付 / 日区分 / 重み / 削除) を
 * 編集する。既定重みは w=[0.5, 0.3, 0.2]。日付変更時は onDateChange(dates) で
 * 呼び出し元に再解決 (GetSimpleForecastEntryDefaults への問い合わせ) を依頼する。
 * 重み編集・行削除はダイアログ内のローカル状態で完結し、「適用」で確定する。
 */
import { Fragment, useEffect, useState } from 'react'

import {
  type DayType,
  type SimpleForecastEntryDefault,
  SimpleForecastMethod,
  type SimpleForecastSourceDateMismatch,
  WeekdayCategory,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import { type Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  Flex,
  HStack,
  Input,
  PrimaryButton,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  TertiaryButton,
  Text,
  Tooltip,
} from '@enechain/ecloud-designsystem'
import {
  FaTrashCanRegular,
  FaTriangleExclamationSolid,
} from '@enechain/ecloud-icons'

import { Badge } from '~/components/common/Badge'
import {
  COMPACT_DATE_FIELD_WIDTH,
  CompactCivilDateField,
} from '~/components/common/CompactCivilDateField'
import { HiddenFieldLabel } from '~/components/common/HiddenFieldLabel'
import { IconOnlyButton } from '~/components/common/IconOnlyButton'
import { Modal } from '~/components/common/Modal'
import { RegionLoading } from '~/components/common/RegionLoading'
import { iconSize } from '~/constants/iconSize'
import {
  type CivilDate,
  civilDateEquals,
  civilDateToInputValue,
  civilDateToProtoDate,
  dateToCivilDate,
  protoDateToCivilDate,
} from '~/models/civilDate'
import { useSingularGroupNameMap } from '~/pages/AdminSingularDay/hooks/useSingularGroupNames'
import { resolveSingularGroupName } from '~/pages/AdminSingularDay/models/options'
import { dayTypeBadgeStatus } from '~/pages/DemandForecastDaily/components/SectionHeader'
import type { UnavailableSourceDates } from '~/pages/DemandForecastDaily/hooks/useListSimpleForecastUnavailableSourceDates'
import { simpleForecastMethodLabel } from '~/pages/DemandForecastDaily/models/simpleForecast'
import {
  SOURCE_DATE_DAY_TYPE_LABEL_KEY,
  type SourceDateRowWarning,
  sourceDateDisplayDayType,
  sourceDateRowWarning,
} from '~/pages/DemandForecastDaily/models/simpleForecastSourceDate'

/** ダイアログ内で編集する Entry 行のローカル working state。 */
type EntryEdit = {
  sourceDate: ProtoDate | undefined
  /**
   * 下の判定 (日付種別・実績なし・候補にならない理由) を backend が解決した日付
   * (= entries 由来の sourceDate)。判定は sourceDate がこれと同じ時だけ使う。
   */
  judgedSourceDate: ProtoDate | undefined
  weekdayCategory: WeekdayCategory
  dayType: DayType
  sourceDateMismatch: SimpleForecastSourceDateMismatch
  actualsMissing: boolean
  isPublicHoliday: boolean
  holidayName: string
  /** 編集用の文字列表現 (number input の value)。適用時に parseFloat する */
  weight: string
}

type Props = {
  isOpen: boolean
  onClose: () => void
  method: SimpleForecastMethod
  /** GetSimpleForecastEntryDefaults の応答 (呼び出し元が保持する最新状態) */
  entries: SimpleForecastEntryDefault[]
  isLoading?: boolean
  /** 「適用」クリック時、確定した Entry 群 (取込確認ダイアログへ渡す) */
  onApply: (entries: Array<{ sourceDate: ProtoDate; weight: number }>) => void
  /**
   * Entry の日付が変更された時に、変更後の日付配列全体を渡す。
   * 呼び出し元はこれを使って GetSimpleForecastEntryDefaults を dates 指定で
   * 再実行し、日タイプ・祝日名を再解決した entries を返す (= 再度 props.entries が更新される)。
   */
  onDateChange: (dates: ProtoDate[]) => void
  /** 対象日の日区分 (平日/土曜/日祝)。AC-DFM-016 の日区分の警告の文言に使う。 */
  targetWeekdayCategory?: WeekdayCategory
  /** 対象日の特異グループ (需要扱いが特異の時だけ)。AC-DFM-016 の文言に使う。 */
  targetSingularGroup?: number
  /** 取込元日付の picker が表示中の範囲の実績の無い日 (AC-DFM-017)。 */
  unavailableSourceDates?: UnavailableSourceDates
  /** 取込元日付の picker の表示月が決まった時 (開いた時・月送り) に呼ぶ。 */
  onSourceDateViewMonthChange?: (year: number, month: number) => void
  /** Defaults query failure resolved to a detailed or generic fallback message. */
  entryDefaultsErrorMessage?: string
}

const DEFAULT_WEIGHTS = ['0.5', '0.3', '0.2']

/**
 * 行の判定 (日付種別・実績なし・候補にならない理由) が今の日付のものか。
 * 未入力行が残る間は再解決されず、同じ日付配列の再解決は cache の同じ応答で entries が
 * 変わらないため、日付を変えた時に判定を消すのではなく、判定した日付と比べる。
 */
function isJudged(e: EntryEdit): boolean {
  return (
    e.sourceDate !== undefined &&
    e.judgedSourceDate !== undefined &&
    e.sourceDate.year === e.judgedSourceDate.year &&
    e.sourceDate.month === e.judgedSourceDate.month &&
    e.sourceDate.day === e.judgedSourceDate.day
  )
}

/** 取込元日付が未入力・判定待ちの行の日区分欄 (design `_renderWeightRows` の中立の「—」)。 */
const NO_SOURCE_DATE_DAY_TYPE = '—'

const NO_UNAVAILABLE_SOURCE_DATES: UnavailableSourceDates = {
  isLoading: false,
  dateKeys: new Set(),
}

/** 対象日の特異グループ名を引いた AC-DFM-016 の文言 (名前の取得は警告が出る時だけ)。 */
const NotSameSingularGroupText = ({
  group,
}: {
  group: number
}): React.ReactElement => {
  const { t } = useTranslation()
  const { groupNames } = useSingularGroupNameMap()
  return (
    <>
      {t(
        'page:demandForecastDaily.simpleForecast.sourceDateWarning.notSameSingularGroup',
        { groupName: resolveSingularGroupName(group, groupNames, t) },
      )}
    </>
  )
}

const SourceDateWarningText = ({
  warning,
}: {
  warning: SourceDateRowWarning
}): React.ReactElement => {
  const { t } = useTranslation()
  switch (warning.kind) {
    case 'noActual':
      return (
        <>{t('page:demandForecastDaily.simpleForecast.noActualSourceDate')}</>
      )
    case 'notSameSingularGroup':
      return <NotSameSingularGroupText group={warning.targetSingularGroup} />
    case 'singularGroupDay':
      return (
        <>
          {t(
            'page:demandForecastDaily.simpleForecast.sourceDateWarning.singularGroupDay',
          )}
        </>
      )
    case 'dayCategory':
      return (
        <>
          {t(
            'page:demandForecastDaily.simpleForecast.sourceDateWarning.dayCategory',
            {
              sourceCategory: t(warning.sourceCategoryLabelKey),
              targetCategory: t(warning.targetCategoryLabelKey),
            },
          )}
        </>
      )
  }
}

export const SimpleForecastSettingsDialog = ({
  isOpen,
  onClose,
  method,
  entries,
  isLoading = false,
  onApply,
  onDateChange,
  targetWeekdayCategory = WeekdayCategory.UNSPECIFIED,
  targetSingularGroup,
  unavailableSourceDates = NO_UNAVAILABLE_SOURCE_DATES,
  onSourceDateViewMonthChange,
  entryDefaultsErrorMessage,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [workingEntries, setWorkingEntries] = useState<EntryEdit[]>([])
  // ユーザーが日付を手で変えた行 (index)。AC-DFM-016 の警告はこの行だけに出す。
  // 再解決中は entries が空になり working state が作り直されるため、別に持つ。
  const [manualRows, setManualRows] = useState<boolean[]>([])
  // AC-DFM-005b/006e: 取込元日付が未入力のまま確定操作をした行があるかどうか。
  // handleApply で確定拒否した時に true にし、エラー表示 + 行の invalid 表示に使う。
  const [hasMissingSourceDateError, setHasMissingSourceDateError] =
    useState(false)

  // entries (呼び出し元の最新状態) が変わるたびに working state を同期する。
  // 既存の重み編集 (workingEntries[i].weight) は index 位置で保持する
  // (= 初回オープン時は workingEntries が空なので既定重みにフォールバック、
  //   日付変更による再解決時は同じ index の重みを保つ)。
  useEffect(() => {
    if (!isOpen) return
    setWorkingEntries((prev) =>
      entries.map((e, i) => ({
        sourceDate: e.sourceDate,
        judgedSourceDate: e.sourceDate,
        weekdayCategory: e.weekdayCategory,
        dayType: e.dayType,
        sourceDateMismatch: e.sourceDateMismatch,
        actualsMissing: e.actualsMissing,
        isPublicHoliday: e.isPublicHoliday,
        holidayName: e.holidayName,
        weight:
          prev[i]?.weight ??
          e.defaultWeight?.value ??
          DEFAULT_WEIGHTS[i] ??
          '0',
      })),
    )
  }, [entries, isOpen])

  // ダイアログを閉じたら working state をクリアする (次回オープン時に entries から再構築)
  useEffect(() => {
    if (!isOpen) {
      setWorkingEntries([])
      setManualRows([])
      setHasMissingSourceDateError(false)
    }
  }, [isOpen])

  const handleDateChange = (
    index: number,
    date: CivilDate | undefined,
  ): void => {
    // AC-DFM-005b/006e: 取込元日付は空にできる (= 未入力行を作れる)。 未入力の
    // まま silent に前の値を保持する旧挙動は改め、 空を明示的に state へ反映
    // した上で確定 (handleApply) 時に拒否する。
    const parsed = date !== undefined ? civilDateToProtoDate(date) : undefined
    const current = protoDateToCivilDate(workingEntries[index]?.sourceDate)
    const changed =
      date === undefined || current === undefined
        ? date !== current
        : !civilDateEquals(date, current)
    if (changed) {
      setManualRows((prev) => {
        // 穴のある配列にしない (filter が穴を飛ばし、削除で別の行に flag がずれる)
        const nextManual = Array.from(
          { length: Math.max(prev.length, index + 1) },
          (_, i) => prev[i] === true,
        )
        nextManual[index] = true
        return nextManual
      })
    }
    const next = workingEntries.map((e, i) =>
      i === index ? { ...e, sourceDate: parsed } : e,
    )
    setWorkingEntries(next)
    const dates = next
      .map((e) => e.sourceDate)
      .filter((d): d is ProtoDate => d !== undefined)
    // 未入力行が 1 つでも残っている間は再解決 (onDateChange) を発火しない。
    // 発火すると呼び出し元が未入力を除いた n-1 件で GetSimpleForecastEntryDefaults
    // を再実行し、応答 entries が n-1 件に縮む → entries を watch する上の useEffect
    // が working state を作り直し、未入力行そのものが消えて handleApply の未入力
    // 検証が本番経路で到達しなくなる (加えて残行の重みが index 一致で別日付へずれる)。
    // 全行が埋まった時点でまとめて再解決する。
    if (dates.length === next.length) {
      onDateChange(dates)
    }
  }

  const handleDelete = (index: number): void => {
    const next = workingEntries.filter((_, i) => i !== index)
    setWorkingEntries(next)
    setManualRows((prev) => prev.filter((_, i) => i !== index))
    // 未入力行を消して全行が埋まり、判定待ちの行が残る時だけ残りの日付で再解決する。
    // 全行が判定済みなら再解決しない (取得中に entries が空になり、重みが既定値で作り直されるため)
    const dates = next
      .map((e) => e.sourceDate)
      .filter((d): d is ProtoDate => d !== undefined)
    if (
      next.length > 0 &&
      dates.length === next.length &&
      next.some((e) => !isJudged(e))
    ) {
      onDateChange(dates)
    }
  }

  // AC-DFM-017: 実績の無い日を取込元にした行が残る間は取り込ませない
  // (取り込んでも AC-DFM-008 で中止される)。
  // 判定の済んでいない行 (日付を消した・再解決待ち) は数えない。未入力は handleApply の AC-DFM-005b で拒否する
  const hasActualsMissing = workingEntries.some(
    (e) => isJudged(e) && e.actualsMissing,
  )
  const canApply =
    !isLoading && entryDefaultsErrorMessage === undefined && !hasActualsMissing

  const sourceDateDayState = (
    d: Date,
  ): { disabled: boolean; hint?: string } | undefined => {
    if (unavailableSourceDates.isLoading) return { disabled: true }
    return unavailableSourceDates.dateKeys.has(
      civilDateToInputValue(dateToCivilDate(d)),
    )
      ? {
          disabled: true,
          hint: t('page:demandForecastDaily.simpleForecast.noActualSourceDate'),
        }
      : undefined
  }

  // spec AC-DFM-005b/006e: 「重みは既定固定 w=[0.5,0.3,0.2] のため P0 では
  // 重み欄のバリデーションは対象外」。frontend はここでは合計/正負検証をせず
  // 入力値をそのまま送り、backend `calcWeightedAvg` が w'ᵢ = wᵢ/Σw の on-the-fly
  // 正規化で受ける (ADR `demand-forecast-weight-normalization-at-defaults`)。
  // Σw ≤ 0 は backend が InvalidArgument で拒否するため、全 0 の需要想定が
  // 黙って保存される事故は起きない。
  //
  // 取込元日付は AC-DFM-005b/006e の対象: 未入力の行があれば確定を拒否し
  // 「取込元日付を入力してください」を表示する (旧「空行を silent に drop」
  // から変更)。
  const handleApply = (): void => {
    if (!canApply) return
    const hasMissingSourceDate = workingEntries.some(
      (e) => e.sourceDate === undefined,
    )
    if (hasMissingSourceDate) {
      setHasMissingSourceDateError(true)
      return
    }
    setHasMissingSourceDateError(false)
    const applied = workingEntries
      .filter((e): e is EntryEdit & { sourceDate: ProtoDate } =>
        Boolean(e.sourceDate),
      )
      .map((e) => ({
        sourceDate: e.sourceDate,
        weight: parseFloat(e.weight),
      }))
      .filter((e) => !isNaN(e.weight))
    onApply(applied)
  }

  const footer = (
    <HStack gap={3}>
      <TertiaryButton onClick={onClose}>
        {t('common:action.cancel')}
      </TertiaryButton>
      <PrimaryButton disabled={!canApply} onClick={handleApply}>
        {t('page:demandForecastDaily.confirmRecordImport.importButton')}
      </PrimaryButton>
    </HStack>
  )

  return (
    <Modal
      footer={footer}
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={t('page:demandForecastDaily.simpleForecast.settingsTitle')}
    >
      <Flex flexDirection="column" gap={3}>
        <Text color="Text.midEmphasis" fontSize="sm">
          {simpleForecastMethodLabel(method, t)}
        </Text>
        {entryDefaultsErrorMessage !== undefined && (
          <Alert
            description={entryDefaultsErrorMessage}
            status="error"
            title=""
          />
        )}
        {/* AC-DFM-005b/006e: 取込元日付が未入力のまま確定操作をした時の拒否表示 */}
        {hasMissingSourceDateError && (
          <Alert
            description={t(
              'page:demandForecastDaily.simpleForecast.sourceDateRequiredError',
            )}
            status="error"
            title=""
          />
        )}
        <RegionLoading isLoading={isLoading}>
          {isLoading ? null : (
            <TableRoot
              // design の表は 14px (`.ecl-table` = --font-size-md)。 Chakra の `size="sm"` は
              // root に `textStyle: "sm"` (12px) を当てるため `fontSize` で打ち消す
              // (DS に table recipe は無く素の Chakra recipe が効く)。 padding は sm のまま。
              fontSize="md"
              aria-label={t(
                'page:demandForecastDaily.simpleForecast.settingsTitle',
              )}
              size="sm"
              style={{ width: '100%', borderCollapse: 'collapse' }}
            >
              <TableHeader>
                <TableRow>
                  <TableColumnHeader>
                    {t('page:demandForecastDaily.simpleForecast.columnDate')}
                  </TableColumnHeader>
                  <TableColumnHeader>
                    {t('page:demandForecastDaily.simpleForecast.columnDayType')}
                  </TableColumnHeader>
                  <TableColumnHeader>
                    {t('page:demandForecastDaily.simpleForecast.columnWeight')}
                  </TableColumnHeader>
                  <TableColumnHeader />
                </TableRow>
              </TableHeader>
              <TableBody>
                {workingEntries.map((entry, index) => {
                  // 日付を消した行・再解決待ちの行は、前の日付の判定 (日付種別・警告) を出さない
                  const isRowJudged = isJudged(entry)
                  const dayType = sourceDateDisplayDayType(entry)
                  const warning = !isRowJudged
                    ? undefined
                    : sourceDateRowWarning(
                        {
                          actualsMissing: entry.actualsMissing,
                          sourceDateMismatch: entry.sourceDateMismatch,
                          weekdayCategory: entry.weekdayCategory,
                          manual: manualRows[index] === true,
                        },
                        {
                          weekdayCategory: targetWeekdayCategory,
                          singularGroup: targetSingularGroup,
                        },
                      )
                  return (
                    // 行削除で数が変わるだけで並び替えは発生しないため index key で十分
                    // (SimilarDayDialog 等の候補行と異なり sourceDate 自体が編集対象で
                    // 一意キーに使えない)
                    <Fragment key={index}>
                      <TableRow>
                        <TableCell>
                          <Box width={COMPACT_DATE_FIELD_WIDTH}>
                            <HiddenFieldLabel
                              htmlFor={`simpleForecastEntry-${index}-sourceDate`}
                            >
                              {t(
                                'page:demandForecastDaily.simpleForecast.columnDate',
                              )}
                            </HiddenFieldLabel>
                            <CompactCivilDateField
                              dayState={sourceDateDayState}
                              hasError={
                                hasMissingSourceDateError &&
                                entry.sourceDate === undefined
                              }
                              id={`simpleForecastEntry-${index}-sourceDate`}
                              onChange={(d) => {
                                handleDateChange(index, d)
                              }}
                              onViewMonthChange={onSourceDateViewMonthChange}
                              value={protoDateToCivilDate(entry.sourceDate)}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          {/* AC-DFM-005a / 006c: 日付種別 4 区分。「祝」は法定祝日だけ (日曜には付けない) */}
                          {!isRowJudged ? (
                            <Badge status="neutral">
                              {NO_SOURCE_DATE_DAY_TYPE}
                            </Badge>
                          ) : (
                            <Flex alignItems="center" gap={1}>
                              <Badge status={dayTypeBadgeStatus(dayType)}>
                                {t(SOURCE_DATE_DAY_TYPE_LABEL_KEY[dayType])}
                              </Badge>
                              {entry.isPublicHoliday &&
                                (entry.holidayName !== '' ? (
                                  <Tooltip
                                    content={entry.holidayName}
                                    placement="top"
                                    showArrow
                                  >
                                    <Badge status="negative">
                                      {t(
                                        'page:demandForecastDaily.simpleForecast.holiday',
                                      )}
                                    </Badge>
                                  </Tooltip>
                                ) : (
                                  <Badge status="negative">
                                    {t(
                                      'page:demandForecastDaily.simpleForecast.holiday',
                                    )}
                                  </Badge>
                                ))}
                            </Flex>
                          )}
                        </TableCell>
                        <TableCell>
                          {/* spec AC-DFM-005b/006e: 「重みは既定固定 w=[0.5,0.3,0.2] のため
                        P0 では重み欄のバリデーションは対象外」。P0 は顧客別
                        カスタマイズ未対応 (P2 で対応) なので重みは編集不可
                        (read-only) にする。編集可 UI にすると画面表示の重みと
                        backend の正規化後の重みが乖離する事故を招く (ADR
                        `demand-forecast-weight-normalization-at-defaults` の
                        「見た値と違う重み」懸念)。P2 で顧客別カスタマイズを
                        入れる際は Input に戻し、backend 正規化との整合戦略を
                        別途決める。 */}
                          <Input
                            aria-label={t(
                              'page:demandForecastDaily.simpleForecast.columnWeight',
                            )}
                            readOnly
                            size="sm"
                            style={{ width: '90px' }}
                            value={entry.weight}
                          />
                        </TableCell>
                        <TableCell>
                          <IconOnlyButton
                            label={t('common:action.delete')}
                            onClick={() => {
                              handleDelete(index)
                            }}
                            size="md"
                            variant="ghost"
                          >
                            <FaTrashCanRegular />
                          </IconOnlyButton>
                        </TableCell>
                      </TableRow>

                      {/* AC-DFM-016 / 017: 行の直下に全幅で出す (design .wt-warn-row) */}
                      {warning !== undefined && (
                        <TableRow>
                          <TableCell colSpan={4}>
                            <Flex
                              textStyle="captionStrong"
                              alignItems="flex-start"
                              gap={1.5}
                              color="Text.warningHigh"
                            >
                              <FaTriangleExclamationSolid
                                boxSize={iconSize.fieldMessage}
                                marginTop="3px"
                                aria-hidden
                              />
                              <Text>
                                <SourceDateWarningText warning={warning} />
                              </Text>
                            </Flex>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  )
                })}
              </TableBody>
            </TableRoot>
          )}
        </RegionLoading>
      </Flex>
    </Modal>
  )
}
